import { v1 } from 'uuid';
import { createUseCRUDService, ICRUDService } from '../../reusableFeatures';
import { backendServices, portalUserService } from '../../services';
import { BusinessDocType, FieldDictionaryUsedBy, RecursivePartial } from '../../types';
import { createFieldDictionaryLayoutViewModel } from './createFieldDictionaryLayoutVM';
import {
    businessDocTypeFormToViewModels,
    QuestionnaireFormData,
    QuestionnaireItem,
    viewModelToBusinessDocTypeForm,
} from './QuestionnaireCRUD.utils';

export const getDefaultQuestionnaireItem = (): QuestionnaireItem => {
    return {
        name: 'Item',
        type: 'TextField',
        id: v1(),
        required: true,
    };
};

export class QuestionnaireCRUDService implements ICRUDService<number, QuestionnaireFormData> {
    private businessDoctypeApi = new backendServices.Apis.BusinessDocTypeApi();

    private documentApi = new backendServices.Apis.DocumentApi();

    get defaultState(): QuestionnaireFormData {
        return {
            name: '',
            description: '',
            items: [getDefaultQuestionnaireItem()],
            businessDocTypeViewModel: {
                BusinessDocTypeId: BusinessDocType.SIMDocument,
                Name: '',
                Description: '',
                CompanyId: portalUserService.getCurrentCompanyId(),
                Active: true,
                Skeleton: '',
            },
            fieldDictionaryLayoutViewModel: createFieldDictionaryLayoutViewModel(),
        };
    }

    get key(): string {
        return 'Questionnaire';
    }

    get invalidateKeys(): string[] {
        return ['QuestionnaireSearch', 'businessDocType'];
    }

    async fetchEntity(businessDocSubTypeId: number): Promise<QuestionnaireFormData> {
        const currentCompanyId = portalUserService.getCurrentCompanyId();

        const {
            data: [businessDocTypeRecord],
        } = await this.businessDoctypeApi.getBusinessDocType({
            $filter: `BusinessDocTypeId eq ${BusinessDocType.SIMDocument} and BusinessDocSubTypeId eq ${businessDocSubTypeId} and companyId eq ${currentCompanyId}`,
        });

        const { data: fieldDictionaryLayoutViewModel } = await this.documentApi.getDocumentLayout({
            businessDocType: BusinessDocType.SIMDocument,
            culture: 'en-US',
            client: FieldDictionaryUsedBy.BuyerPortal,
            documentClass: businessDocSubTypeId.toString(),
            buyerCompanyId: currentCompanyId,
        });

        return viewModelToBusinessDocTypeForm(businessDocTypeRecord, fieldDictionaryLayoutViewModel);
    }

    private updateFieldDictionaryLayoutViewModel(
        fieldDictionaryLayoutViewModel: RecursivePartial<backendServices.ViewModels.DocumentLayoutViewModel>,
        fieldDictionaryItems: RecursivePartial<backendServices.ViewModels.FieldDictionaryViewModel>[],
        businessDocSubTypeId: number
    ): RecursivePartial<backendServices.ViewModels.DocumentLayoutViewModel> {
        const fieldDictionaryLayoutViewModelUpdate = {
            ...fieldDictionaryLayoutViewModel,
            Layouts: fieldDictionaryLayoutViewModel.Layouts!.map((layout) => {
                const relevantCell = layout.Cells![0];

                return {
                    ...layout,
                    Cells: [
                        {
                            ...relevantCell,
                            Fields: fieldDictionaryItems.map((item, index) => ({
                                ...item,
                                FieldDictionaryLayoutCellId: relevantCell.FieldDictionaryLayoutCellId,
                                SIMDocumentClass: businessDocSubTypeId,
                                DisplayOrder: 10 * index,
                            })),
                        },
                    ],
                };
            }),
        };

        return fieldDictionaryLayoutViewModelUpdate;
    }

    async createEntity(state: QuestionnaireFormData): Promise<number> {
        const [businessDocTypeViewModel, fieldDictionaryItems] = businessDocTypeFormToViewModels(state);

        const res = await this.businessDoctypeApi.postBusinessDocType(businessDocTypeViewModel);

        const businessDocSubTypeId = res.data.SubID;

        const { fieldDictionaryLayoutViewModel } = state;

        const fieldDictionaryLayoutViewModelUpdate = this.updateFieldDictionaryLayoutViewModel(
            fieldDictionaryLayoutViewModel,
            fieldDictionaryItems,
            businessDocSubTypeId
        );

        await this.documentApi.postDocumentLayout(
            {
                businessDocType: BusinessDocType.SIMDocument,
                documentClass: businessDocSubTypeId,
                buyerCompanyId: portalUserService.getCurrentCompanyId(),
            },
            fieldDictionaryLayoutViewModelUpdate
        );

        return businessDocSubTypeId;
    }

    async updateEntity(businessDocSubTypeId: number, state: QuestionnaireFormData): Promise<void> {
        const [businessDocTypeViewModel, fieldDictionaryItems] = businessDocTypeFormToViewModels(state);

        await this.businessDoctypeApi.putBusinessDocType(
            { businessDocTypeId: BusinessDocType.SIMDocument, businessDocSubTypeId },
            businessDocTypeViewModel
        );

        const { fieldDictionaryLayoutViewModel } = state;

        const fieldDictionaryLayoutViewModelUpdate = this.updateFieldDictionaryLayoutViewModel(
            fieldDictionaryLayoutViewModel,
            fieldDictionaryItems,
            businessDocSubTypeId
        );

        await this.documentApi.postDocumentLayout(
            {
                businessDocType: BusinessDocType.SIMDocument,
                documentClass: businessDocSubTypeId,
                buyerCompanyId: portalUserService.getCurrentCompanyId(),
            },
            fieldDictionaryLayoutViewModelUpdate
        );
    }

    async deleteEntity(businessDocSubTypeId: number): Promise<void> {
        await this.businessDoctypeApi.deleteBusinessDocType({
            businessDocTypeId: BusinessDocType.SIMDocument,
            businessDocSubTypeId,
        });

        await this.documentApi.deleteDocumentLayout({
            businessDocType: BusinessDocType.SIMDocument,
            businessDocSubType: businessDocSubTypeId,
            buyerCompanyId: portalUserService.getCurrentCompanyId(),
        });
    }
}

export const useQuestionnaireCRUDService = createUseCRUDService(QuestionnaireCRUDService);
